// FLAG:useDocuAi — the browser half of the UI-delivery guarantee
// (plans/usedocuai-guaranteed-ui-delivery, T3).
//
// Terminal FILE:ANALYSED broadcasts used to be fire-and-forget: no ack, no replay, so one lost
// event left the blinking AI badge standing until the client backstop (ANALYZING_MAX_MS = 5 min)
// capped it. Each terminal event now carries an `eventId` and leaves an outstanding row on the
// server; posting the eventId back clears it, and anything still outstanding gets re-broadcast.
//
// ══ TWO ACKS, TWO MEANINGS — do not collapse them ══
// 1. RECEIPT (T7): WebSocketContext acks EVERY incoming frame carrying an eventId, from the one
//    always-mounted place in the app. This is what STOPS the resend, because the resend exists to
//    recover LOST messages — not to compensate for a page that has no interested component. All
//    six per-host consumers are page-specific, so without this a user sitting on the Dashboard
//    would burn the whole attempt budget and trigger a warn asserting a defect that isn't there.
// 2. PROCESSED (T3): each consumer acks after APPLYING the state. This is the semantic signal
//    and it stays mandatory.
//
// ══ EVERY consumer of FILE:ANALYSED MUST STILL ACK ══
// Not just the Documents list. `expense-list.tsx` handles the SAME event, and any future host
// will too. The global receipt ack does NOT excuse a consumer from acking — it only means a
// missing consumer-ack no longer causes pointless re-broadcasts. So this helper exists to be
// reused, never copy-pasted. If you add a new FILE:ANALYSED consumer, call ackUiEvent() from it.
// Acking twice is free: ids are de-duplicated into one batch and the server ack is idempotent.
//
// Two rules for the call site:
//  1. Ack AFTER applying the state, never before — acking first would confirm something the host
//     has not actually processed yet, which is exactly the guarantee this replaces.
//  2. Don't ack per event. Acks coalesce into one request per flush window, so a mass upload
//     cannot produce a request storm (the tax-category request-storm lesson).
import FileService from '@/modules/file/fileService'

// Server caps the batch at 200 eventIds (routes/document/uiEventAck.ts MAX_EVENT_IDS); stay under
// it so a huge burst is split into several requests instead of being rejected wholesale.
const MAX_BATCH = 200
// Short enough to be invisible to a user watching a tile, long enough that a mass upload's worth
// of terminals collapses into a single request.
const FLUSH_DELAY_MS = 250

const pending = new Set<string>()
let flushTimer: ReturnType<typeof setTimeout> | null = null

const flush = async(): Promise<void> => {
  flushTimer = null
  if (!pending.size) return
  // Take the batch out of `pending` BEFORE awaiting, so events arriving during the request are
  // collected for the next flush instead of being dropped or double-sent.
  //
  // Array.from, NOT `[...pending]` — and this is a correctness requirement, not a style choice.
  // webpack.config.common.js builds the app with @babel/preset-env `loose: true`, and in loose
  // mode array spread compiles to `[].concat(pending)`. `Array.prototype.concat` does not iterate
  // a Set: it appends the Set OBJECT as a single element. So `[...pending]` produced `[Set]`,
  // which serialised to `{"eventIds":[{}]}` and was rejected by the route's ajv guard with 400
  // NO_STRING. Worse, `pending.delete(theSet)` then removed nothing, so `pending` never drained
  // and the `if (pending.size) schedule()` tail below re-armed forever — an endless stream of
  // failing POSTs, and not one delivery ever acked. Observed live in Chrome, not theorised.
  // Array.from is a real runtime call and iterates the Set correctly under the same build.
  // Full write-up incl. the other two sites this bit: docs/babel-loose-spread-hazard.md.
  const batch = Array.from(pending).slice(0, MAX_BATCH)
  batch.forEach(id => pending.delete(id))

  try {
    await FileService.ackUiEvents(batch)
  } catch {
    // Deliberately swallowed and NOT re-queued. A failed ack is self-healing: the row stays
    // outstanding, the server re-broadcasts, and the next delivery produces a fresh ack attempt.
    // Re-queueing here could spin against a persistently failing endpoint for no benefit.
  }

  // More arrived while we were in flight (or the burst exceeded MAX_BATCH) → schedule again.
  if (pending.size) schedule()
}

const schedule = (): void => {
  if (flushTimer) return
  // .catch keeps this from ever becoming an unhandled rejection; flush() already swallows the
  // request error itself, so this is belt-and-braces for the scheduling path.
  flushTimer = setTimeout(() => { flush().catch(() => {}) }, FLUSH_DELAY_MS)
}

/**
 * Confirm that a terminal FILE:ANALYSED event was APPLIED by this host.
 *
 * Safe to call from several hosts for the same event — the ids are de-duplicated into one batch,
 * and the server-side ack is idempotent (a second ack simply matches no row).
 *
 * @param eventId the `eventId` from the ws payload; absent on non-ledgered events (e.g. the start
 *   broadcast) and on flag-OFF payloads, in which case this is a no-op.
 */
export const ackUiEvent = (eventId?: string | null): void => {
  if (!eventId || typeof eventId !== 'string') return
  pending.add(eventId)
  schedule()
}

// Test seam only — lets a spec flush synchronously instead of waiting out FLUSH_DELAY_MS.
export const __flushUiEventAcksForTest = async(): Promise<void> => {
  if (flushTimer) {
    clearTimeout(flushTimer)
    flushTimer = null
  }
  await flush()
}

export const __resetUiEventAcksForTest = (): void => {
  if (flushTimer) {
    clearTimeout(flushTimer)
    flushTimer = null
  }
  pending.clear()
}
